(() => {
  const root = document.querySelector('[data-compatibility-tool]');
  const data = window.SkinpediaCompatibilityData;
  if (!root || !data || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';

  const products = Array.isArray(data.products) ? data.products : [];
  const rules = Array.isArray(data.rules) ? data.rules : [];
  const form = root.querySelector('[data-compatibility-form]');
  const submit = root.querySelector('[data-find-out]');
  const result = root.querySelector('[data-compatibility-result]');
  const live = root.querySelector('[data-compatibility-live]');
  const fields = Array.from(root.querySelectorAll('[data-product-field]'));

  const normalize = (value) => value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const selected = new Map();

  const statusCopy = {
    compatible: { symbol: '✓', label: 'Compatible' },
    caution: { symbol: '◐', label: 'Use With Caution' },
    separate: { symbol: '↔', label: 'Better Used Separately' }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const hasAny = (product, categories) => categories.some((category) => product.activeCategories.includes(category));
  const isLeaveOn = (product) => product.exposure === 'leave-on';
  const matchesRule = (rule, first, second) => {
    if (rule.requireBothLeaveOn && (!isLeaveOn(first) || !isLeaveOn(second))) return false;
    if (rule.sameCategory) return first.activeCategories.includes(rule.sameCategory) && second.activeCategories.includes(rule.sameCategory);
    if (!rule.groups) return false;
    const [left, right] = rule.groups;
    return (hasAny(first, left) && hasAny(second, right)) || (hasAny(first, right) && hasAny(second, left));
  };

  const resolveGuidance = (first, second) => {
    const rule = rules.slice().sort((a, b) => b.priority - a.priority).find((candidate) => matchesRule(candidate, first, second));
    if (rule) return rule;
    if (first.activeCategories.includes('sunscreen') || second.activeCategories.includes('sunscreen')) {
      return {
        id: 'sunscreen-default', status: 'compatible',
        explanation: 'No reviewed category-level conflict is identified. Use the sunscreen as the final morning skincare step and reapply it as directed.',
        suggestedUse: 'Same routine; sunscreen last in the morning'
      };
    }
    const gentleTags = ['gentle-cleanser', 'moisturizer', 'barrier-support', 'hydrating'];
    if (hasAny(first, gentleTags) || hasAny(second, gentleTags)) {
      return {
        id: 'gentle-support-default', status: 'compatible',
        explanation: 'No reviewed category-level conflict is identified. This cleansing, hydrating, or barrier-support step can generally be used in the same routine.',
        suggestedUse: 'Same routine'
      };
    }
    return {
      id: 'general-default', status: 'compatible',
      explanation: 'No reviewed category-level conflict is identified for these products. They can generally be used in the same routine if your skin tolerates each product.',
      suggestedUse: 'Same routine'
    };
  };

  const orderRank = (product) => {
    if (product.exposure === 'rinse-off') return 10;
    if (/toner/i.test(product.productType)) return 20;
    if (product.activeCategories.includes('anhydrous')) return 38;
    if (/serum|treatment|gel/i.test(product.productType)) return 30;
    if (product.activeCategories.includes('sunscreen')) return 50;
    if (product.activeCategories.includes('moisturizer')) return 40;
    if (/cream|balm|suspension/i.test(product.productType)) return 40;
    return null;
  };

  const applicationOrder = (first, second, guidance) => {
    if (guidance.status === 'separate') return [];
    const firstRank = orderRank(first);
    const secondRank = orderRank(second);
    if (firstRank === null || secondRank === null || firstRank === secondRank) return [];
    return firstRank < secondRank ? [first.name, second.name] : [second.name, first.name];
  };

  const updateSubmit = () => {
    const ready = fields.every((field) => selected.has(field.dataset.productField));
    submit.disabled = !ready;
    submit.setAttribute('aria-disabled', String(!ready));
  };

  const closeList = (field) => {
    const input = field.querySelector('[data-product-input]');
    const list = field.querySelector('[data-product-list]');
    list.hidden = true;
    list.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    field.dataset.activeIndex = '-1';
  };

  const chooseProduct = (field, product) => {
    const key = field.dataset.productField;
    const input = field.querySelector('[data-product-input]');
    const clear = field.querySelector('[data-product-clear]');
    selected.set(key, product);
    input.value = `${product.brand} — ${product.name}`;
    input.dataset.selectedId = product.id;
    clear.hidden = false;
    closeList(field);
    updateSubmit();
    result.hidden = true;
    live.textContent = `${product.name} selected for ${input.labels[0].textContent}.`;
  };

  const renderMatches = (field) => {
    const input = field.querySelector('[data-product-input]');
    const list = field.querySelector('[data-product-list]');
    const query = normalize(input.value.trim());
    const otherSelection = Array.from(selected.entries()).find(([key]) => key !== field.dataset.productField)?.[1];
    const matches = query ? products.filter((product) => product.id !== otherSelection?.id && normalize(`${product.brand} ${product.name} ${product.productType} ${product.keyActiveIngredients.join(' ')}`).includes(query)).slice(0, 7) : [];
    list.innerHTML = '';
    field.dataset.activeIndex = '-1';
    if (!query) return closeList(field);
    if (!matches.length) {
      list.innerHTML = '<li class="compatibility-option compatibility-option--empty">No products found in the current database.</li>';
    } else {
      matches.forEach((product, index) => {
        const option = document.createElement('li');
        option.id = `${input.id}-option-${index}`;
        option.className = 'compatibility-option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.productId = product.id;
        option.innerHTML = `<span>${escapeHtml(product.name)}</span><small>${escapeHtml(product.brand)} · ${escapeHtml(product.productType)}</small>`;
        option.addEventListener('mousedown', (event) => event.preventDefault());
        option.addEventListener('click', () => chooseProduct(field, product));
        list.append(option);
      });
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const moveActive = (field, direction) => {
    const input = field.querySelector('[data-product-input]');
    const options = Array.from(field.querySelectorAll('[role="option"]'));
    if (!options.length) return;
    let index = Number(field.dataset.activeIndex || -1);
    index = (index + direction + options.length) % options.length;
    options.forEach((option, optionIndex) => option.setAttribute('aria-selected', String(optionIndex === index)));
    field.dataset.activeIndex = String(index);
    input.setAttribute('aria-activedescendant', options[index].id);
    options[index].scrollIntoView({ block: 'nearest' });
  };

  fields.forEach((field) => {
    const key = field.dataset.productField;
    const input = field.querySelector('[data-product-input]');
    const clear = field.querySelector('[data-product-clear]');
    input.addEventListener('input', () => {
      if (selected.has(key)) selected.delete(key);
      delete input.dataset.selectedId;
      clear.hidden = !input.value;
      result.hidden = true;
      updateSubmit();
      renderMatches(field);
    });
    input.addEventListener('focus', () => { if (input.value && !selected.has(key)) renderMatches(field); });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (field.querySelector('[data-product-list]').hidden) renderMatches(field);
        moveActive(field, event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Enter') {
        const index = Number(field.dataset.activeIndex || -1);
        const option = field.querySelectorAll('[role="option"]')[index];
        if (option) {
          event.preventDefault();
          chooseProduct(field, products.find((product) => product.id === option.dataset.productId));
        }
      } else if (event.key === 'Escape') {
        closeList(field);
      }
    });
    input.addEventListener('blur', () => setTimeout(() => closeList(field), 120));
    clear.addEventListener('click', () => {
      selected.delete(key);
      input.value = '';
      delete input.dataset.selectedId;
      clear.hidden = true;
      closeList(field);
      result.hidden = true;
      updateSubmit();
      input.focus();
      live.textContent = `${input.labels[0].textContent} cleared.`;
    });
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submit.disabled) return;
    const first = selected.get('product-1');
    const second = selected.get('product-2');
    const rule = resolveGuidance(first, second);
    const status = statusCopy[rule.status];
    const resolvedOrder = applicationOrder(first, second, rule);
    const order = resolvedOrder.length ? `<div class="compatibility-result__detail"><span>Application order</span><p>${resolvedOrder.map(escapeHtml).join(' <b aria-hidden="true">→</b> ')}</p></div>` : '';
    const shopButtons = [first, second].filter((product) => product.soldBySkinSource && product.shopifyProductUrl).map((product) => `<a class="button button--secondary" href="${escapeHtml(product.shopifyProductUrl)}">Shop ${escapeHtml(product.name)} at Skin Source</a>`).join('');
    result.className = `compatibility-result compatibility-result--${rule.status}`;
    result.innerHTML = `
      <p class="compatibility-result__eyebrow">${escapeHtml(first.name)} + ${escapeHtml(second.name)}</p>
      <div class="compatibility-result__status"><span aria-hidden="true">${status.symbol}</span><h2>${status.label}</h2></div>
      <div class="compatibility-result__why"><span>Why</span><p>${escapeHtml(rule.explanation)}</p></div>
      <div class="compatibility-result__details">
        <div class="compatibility-result__detail"><span>How to use them</span><p>${escapeHtml(rule.suggestedUse)}</p></div>
        ${order}
      </div>
      ${shopButtons ? `<div class="compatibility-result__shops">${shopButtons}</div>` : ''}
      <p class="compatibility-result__disclaimer">Your skin is unique. These recommendations are general guidance based on product ingredients and usage considerations, not medical advice. Patch test new products, introduce strong actives gradually, and consult a dermatologist for persistent irritation or skin concerns.</p>`;
    result.hidden = false;
    result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    live.textContent = `${status.label}. ${rule.explanation}`;
  });

  updateSubmit();
})();
